import React from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { PrismicImage } from '@prismicio/react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { PageSlice, SliceIconDescriptionBoxesDetails, SliceIconDescriptionBoxesItem } from '@contracts';
import ExternalLink from '@images/vms-account/vms-icons/ExternalLink.svg';
import Carousel from './Carousel';
import LinkTranslated from './LinkTranslated';
import { SFDBodyLGSFMBodyMD, SFDHeading1SFMHeading2, SFDHeading6 } from './Typography';
import { TextVariant, Typography } from '@components/shared/design/Typography';

interface Props {
  slice: PageSlice;
}

const BREAKPOINT = 1180;

const IconDescriptionBoxes = ({ slice }: Props) => {
  const { t } = useTranslation();
  const sliceDetails = slice.primary as SliceIconDescriptionBoxesDetails;
  const sliceTitle = t(sliceDetails.heading_text_key);
  const sliceIcon = sliceDetails.heading_icon;
  const sliceItems = slice.items as SliceIconDescriptionBoxesItem[];
  const itemWidth = sliceDetails.column_max_width?.text ?? '270px';
  const titleType = sliceIcon?.url ? 'h6' : 'h1';
  const imageWidth = sliceDetails?.icon_max_width || '70px';
  const imageMobileWidth = sliceDetails?.icon_max_width || '50px';
  const isMobileSlider = !!sliceDetails?.is_mobile_slider;
  const mainTextFontSize = sliceDetails?.main_text_font_size || '18px';
  const mobileMainTextFontSize = sliceDetails?.mobile_main_text_font_size || '13px';

  const flickityOptions = {
    initialIndex: 1,
    groupCells: 1,
    pageDots: true,
    prevNextButtons: false,
    contain: true,
    percentPosition: false,
    cellAlign: 'center',
    watchCSS: false,
    autoPlay: false,
    draggable: true
  };

  return (
    <Container $padding={sliceDetails?.padding_style} $background={sliceDetails?.background_style}>
      <TitleWrapper $titleType={titleType}>
        {sliceIcon?.url ? (
          <HeadingIconWrapper>
            <PrismicImage field={sliceIcon} />
            <SFDHeading6 className={titleType}>{sliceTitle}</SFDHeading6>
          </HeadingIconWrapper>
        ) : (
          <SFDHeading1SFMHeading2 className={titleType}>{sliceTitle}</SFDHeading1SFMHeading2>
        )}
      </TitleWrapper>
      {isMobileSlider ? (
        <MobileSliderItemsWrapper>
          <Carousel options={flickityOptions}>
            {sliceItems.map((sliceItem) => (
              <ItemWrapper key={`idb-item-${sliceItem.main_text_key}`} $width={itemWidth} $isMobileSlider={true}>
                <ItemIconWrapper $width={imageWidth} $mobileWidth={imageMobileWidth} $isMobileSlider={true}>
                  <LazyLoadImage src={sliceItem?.icon?.url} alt={sliceItem?.icon?.alt || ''} />
                </ItemIconWrapper>
                {sliceItem?.huge_text_key ? <ItemHugeTextWrapper>{t(sliceItem.huge_text_key)}</ItemHugeTextWrapper> : null}
                <ItemTextWrapper $isMobileSlider={true}>
                  <MainTextWrapper $fontSize={mainTextFontSize} $mobileFontSize={mobileMainTextFontSize} $isMobileSlider={true}>
                    <SFDBodyLGSFMBodyMD>{t(sliceItem.main_text_key || '')}</SFDBodyLGSFMBodyMD>
                  </MainTextWrapper>
                  {sliceItem?.link_text_key ? (
                    <LinkTranslated className="source-link" to={sliceItem?.link?.url || ''} target={sliceItem?.link?.target}>
                      <span>{t(sliceItem?.link_text_key)}</span>
                      <ExternalLink style={{ width: 12, flexShrink: 0 }} />
                    </LinkTranslated>
                  ) : null}
                </ItemTextWrapper>
              </ItemWrapper>
            ))}
          </Carousel>
        </MobileSliderItemsWrapper>
      ) : null}
      <DesktopItemsWrapper $isMobileSlider={isMobileSlider}>
        <ItemsWrapper>
          {sliceItems.map((sliceItem) => (
            <ItemWrapper key={`idb-item-${sliceItem.main_text_key}`} $width={itemWidth}>
              <ItemIconWrapper $width={imageWidth} $mobileWidth={imageMobileWidth}>
                <LazyLoadImage src={sliceItem?.icon?.url} alt={sliceItem?.icon?.alt || ''} />
              </ItemIconWrapper>

              {sliceItem?.huge_text_key ? <ItemHugeTextWrapper>{t(sliceItem.huge_text_key)}</ItemHugeTextWrapper> : null}
              <ItemTextWrapper>
                {sliceItem?.title_text_key ? (
                  <Typography $variant={TextVariant.Heading6} $mobileVariant={TextVariant.BodyBoldLG}>
                    {t(sliceItem.title_text_key)}
                  </Typography>
                ) : null}
                <MainTextWrapper $fontSize={mainTextFontSize} $mobileFontSize={mobileMainTextFontSize}>
                  <SFDBodyLGSFMBodyMD>{t(sliceItem.main_text_key || '')}</SFDBodyLGSFMBodyMD>
                </MainTextWrapper>
                {sliceItem?.link_text_key ? (
                  <LinkTranslated className="source-link" to={sliceItem?.link?.url || ''} target={sliceItem?.link?.target}>
                    <span>{t(sliceItem?.link_text_key)}</span>
                    <ExternalLink style={{ width: 12, flexShrink: 0 }} />
                  </LinkTranslated>
                ) : null}
              </ItemTextWrapper>
            </ItemWrapper>
          ))}
        </ItemsWrapper>
      </DesktopItemsWrapper>
    </Container>
  );
};

export default IconDescriptionBoxes;

const Container = styled.div<{ $padding: string; $background: string }>`
  background: ${(props) => props.$background};
  padding: ${(props) => props.$padding || '0'};
  color: ${Colors.navyBlue};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0 30px;
  }
`;

const HeadingIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  & img {
    width: 20px;
    height: auto;
  }
`;

const TitleWrapper = styled.div<{ $titleType: string }>`
  margin-bottom: ${(props) => (props.$titleType === 'h1' ? '60px' : '40px')};
  display: flex;
  justify-content: center;
  text-align: center;

  @media (max-width: ${BREAKPOINT}px) {
    margin-bottom: 40px;

    div.h1 {
      font-size: 32px;
    }
  }

  @media (max-width: 768px) {
    margin-bottom: 24px;

    div.h1 {
      font-size: 24px;
    }
  }
`;

const ItemsWrapper = styled.div`
  display: flex;
  gap: 40px;
  display: flex;
  justify-content: center;
  align-self: center;

  @media (max-width: 1300px) {
    gap: 20px;
  }

  @media (max-width: ${BREAKPOINT}px) {
    flex-direction: column;
    width: 500px;
  }

  @media (max-width: 768px) {
    width: 340px;
  }

  @media (max-width: 400px) {
    width: 100%;
  }
`;

const MobileSliderItemsWrapper = styled.div`
  display: none;
  width: 100%;
  margin: 0 auto;
  margin-bottom: 40px;

  .carousel:after {
    content: 'flickity';
    display: none; /* hide :after */
  }

  .flickity-viewport {
    overflow: visible !important;
  }

  @media (max-width: ${BREAKPOINT}px) {
    display: block;
  }
`;

const DesktopItemsWrapper = styled.div<{ $isMobileSlider?: boolean }>`
  display: flex;
  justify-content: center;

  @media (max-width: ${({ $isMobileSlider }) => (!$isMobileSlider ? 0 : BREAKPOINT)}px) {
    display: none;
  }
`;

const ItemWrapper = styled.div<{ $width: string; $isMobileSlider?: boolean }>`
  display: flex;
  border-radius: 20px;
  border: 1px solid ${Colors.ecru};
  background: ${Colors.ecru50};
  padding: 30px 32px;
  gap: 20px;
  width: ${(props) => props.$width || '270px'};
  flex-direction: column;
  align-items: center;

  @media (max-width: ${({ $isMobileSlider }) => ($isMobileSlider ? 0 : BREAKPOINT)}px) {
    width: 100%;
    border: none;
    background: none;
    padding: 0;
    gap: 20px;
    flex-direction: row;
  }

  ${({ $isMobileSlider }) =>
    $isMobileSlider
      ? `
      &:not(:last-child) {
        margin-right: 24px;
      }`
      : 0}
`;

const ItemIconWrapper = styled.div<{ $width: string; $mobileWidth: string; $isMobileSlider?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: ${(props) => props.$width};
    max-width: ${(props) => props.$width};
  }

  @media (max-width: ${({ $isMobileSlider }) => ($isMobileSlider ? 0 : BREAKPOINT)}px) {
    img {
      width: ${(props) => props.$mobileWidth};
      max-width: ${(props) => props.$mobileWidth};
    }
  }
`;

const MainTextWrapper = styled.div<{ $fontSize: string; $mobileFontSize: string; $isMobileSlider?: boolean }>`
  p {
    font-size: ${(props) => props.$fontSize};
  }

  @media (max-width: ${({ $isMobileSlider }) => ($isMobileSlider ? 0 : BREAKPOINT)}px) {
    p {
      font-size: ${(props) => props.$mobileFontSize};
    }
  }
`;

const ItemTextWrapper = styled.div<{ $isMobileSlider?: boolean }>`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;

  @media (max-width: ${({ $isMobileSlider }) => ($isMobileSlider ? 0 : BREAKPOINT)}px) {
    justify-content: center;
    align-items: flex-start;
    text-align: left;
    gap: 5px;
  }

  a.source-link {
    display: flex;
    align-items: center;
    gap: 5px;

    &:hover {
      text-decoration: none;
      opacity: 0.6;
    }

    span {
      font-family: BrownStd;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 140%; /* 19.6px */
      color: ${Colors.lilac};
    }

    svg path {
      fill: ${Colors.lilac};
    }
  }
`;

const ItemHugeTextWrapper = styled.div`
  text-align: center;
  color: ${Colors.navyBlue};
  font-family: Domaine;
  font-size: 50px;
  font-style: normal;
  font-weight: 600;
  line-height: 110%;
  letter-spacing: -0.5px;
  margin-bottom: -10px;

  @media (max-width: 768px) {
    font-size: 34px;
    font-style: normal;
    font-weight: 600;
    line-height: 110%;
    letter-spacing: -0.34px;
  }
`;
